import _ from 'lodash'
import { Spx, ThemeFontMap } from '@wix/thunderbolt-becky-types'
import { color, resolveSpxValue } from '@wix/thunderbolt-commons'
import type {
	Size,
	Color,
	ThemeColor,
	OldFont,
	BoxShadow,
	BoxShadowLayer,
	OldStyle,
	Style,
	Opacity,
	BackgroundFill,
	Border,
	TextShadow,
	CssValue,
	TextDecorationLine,
	OldStylePropItem,
	StylePropItem,
} from './types'
import { parseFontStr } from '../fonts/fontsParser'
import { fontConverter } from './Font/fontPropToCss'
import { basicStyleConvertersMap } from './common/commonPropsToCss'
import { Font } from './Font/types'
import { KeywordType, Length, Number } from './common/type'

const unitRegex = /[a-zA-Z%]+$/

const unitValueConverter = (
	styleProp: Size,
	isScalePropExperimentOn: boolean = false,
	themeSpx: Spx | undefined = undefined
) => {
	if (styleProp.unit === 'spx' && isScalePropExperimentOn) {
		return resolveSpxValue(`${styleProp.value}spx`, themeSpx)
	}

	return `${styleProp.value}${styleProp.unit}`
}

const colorConverter = (styleProp: Color): string => color.getSplitRgbValuesStringWithoutAlpha(styleProp.value)!

const themeColorConverter = (styleProp: ThemeColor) => `var(--${styleProp.theme})`

const fontOldConverter = (
	styleProp: OldFont,
	themeFontMap: ThemeFontMap,
	isScalePropExperimentOn: boolean = false,
	themeSpx: Spx | undefined = undefined
) => {
	const { style, variant, weight, size, lineHeight, family, theme } = styleProp

	const fixedSize = size ? unitValueConverter(size, isScalePropExperimentOn, themeSpx) : '0'
	const fixedLineHeight = lineHeight ? unitValueConverter(lineHeight, isScalePropExperimentOn, themeSpx) : ''

	if (theme) {
		const themeFontValue = themeFontMap[theme]
		const themeFontObject = parseFontStr(themeFontValue)
		let themeSize = unitValueConverter(
			{ value: themeFontObject.size, unit: themeFontObject.sizeUnit, propertyType: 'Size' },
			isScalePropExperimentOn,
			themeSpx
		)

		let themeLineHeight = unitValueConverter(
			{
				value: parseFloat(themeFontObject.lineHeight),
				unit: themeFontObject.lineHeight.match(unitRegex)?.[0],
				propertyType: 'Size',
			},
			isScalePropExperimentOn,
			themeSpx
		)

		Object.entries(styleProp).forEach(([key, value]) => {
			switch (key) {
				case 'size':
					themeSize = fixedSize
					break
				case 'lineHeight':
					themeLineHeight = fixedLineHeight
					break
				default:
					_.set(themeFontObject, [key], value)
			}
		})

		return `${themeFontObject.style} ${themeFontObject.variant} ${themeFontObject.weight} ${themeSize}/${themeLineHeight} ${themeFontObject.family}`
	}

	return `${style} ${variant} ${weight} ${fixedSize}/${fixedLineHeight} ${family}`
}

const boxShadowConverter = (styleProp: BoxShadow) => {
	const layers = styleProp.layers
	return layers
		.map((layer: BoxShadowLayer) => {
			const { inset, offsetX, offsetY, blurRadius, spreadRadius, color: layerColor } = layer
			const insetStr = inset ? 'inset ' : ''
			const offsetXStr = unitValueConverter(offsetX)
			const offsetYStr = unitValueConverter(offsetY)
			const blurRadiusStr = blurRadius ? unitValueConverter(blurRadius) : ''
			const spreadRadiusStr = spreadRadius ? unitValueConverter(spreadRadius) : ''
			const colorStr = layerColor
				? layerColor.propertyType === 'Color'
					? layerColor.value
					: themeColorConverter(layerColor)
				: ''
			return `${insetStr}${offsetXStr} ${offsetYStr} ${blurRadiusStr} ${spreadRadiusStr} ${colorStr}`
		})
		.join(', ')
}

const opacityConverter = (styleProp: Opacity) => `${styleProp.value}`

const backgroundFillConverter = (styleProp: BackgroundFill) =>
	styleProp.colorOrGradient.propertyType === 'Color'
		? styleProp.colorOrGradient.value
		: themeColorConverter(styleProp.colorOrGradient)

const borderConverter = (styleProp: Border) => {
	const { width, style, color: borderColor } = styleProp
	const widthStr = unitValueConverter(width)
	const colorStr = borderColor.propertyType === 'Color' ? borderColor.value : themeColorConverter(borderColor)
	return `${widthStr} ${style} ${colorStr}`
}

const textShadowConverter = (styleProp: TextShadow) => {
	const { offsetX, offsetY, blur, color: layerColor } = styleProp
	const offsetXStr = unitValueConverter(offsetX)
	const offsetYStr = unitValueConverter(offsetY)
	const blurRadiusStr = blur ? unitValueConverter(blur) : ''
	const colorStr = layerColor
		? layerColor.propertyType === 'Color'
			? layerColor.value
			: themeColorConverter(layerColor)
		: ''
	return `${offsetXStr} ${offsetYStr} ${blurRadiusStr} ${colorStr}`
}

const textDecorationLineConverter = (styleProp: TextDecorationLine) => styleProp.value.join(' ')

const isNewStylePropItem = (prop: OldStylePropItem | StylePropItem): prop is StylePropItem =>
	(prop as StylePropItem).type !== undefined

export const oldStylePropsToCssVars = (
	style: any,
	themeFontMap: ThemeFontMap,
	isScalePropExperimentOn: boolean = false,
	themeSpx: Spx | undefined = undefined
): Record<string, string | number> => {
	const styleProps = (style as OldStyle).style.properties || (style as OldStyle).style
	const { spx } = style
	const cssVars = _.reduce(
		styleProps,
		(acc: Record<string, string | number>, value, prop) => {
			const styleType =
				typeof value === 'object' ? (isNewStylePropItem(value) ? value.type : value.propertyType) : undefined

			switch (styleType) {
				case 'Size':
					acc[`--${prop}`] = unitValueConverter(value as Size, isScalePropExperimentOn, themeSpx)
					return acc
				case 'Color':
					acc[`--${prop}`] = colorConverter(value as Color)
					if ((value as Color).value === 'transparent') {
						// set alpha channel to 0 explicitly when the color is transparent
						acc[`--alpha-${prop}`] = 0
					} else if (_.isUndefined(styleProps[`alpha-${prop}`])) {
						acc[`--alpha-${prop}`] = color.getColorAlpha((value as Color).value)!
					}
					return acc
				case 'ThemeColor':
					acc[`--${prop}`] = themeColorConverter(value as ThemeColor)
					if ((value as ThemeColor).alpha) {
						acc[`--alpha-${prop}`] = (value as ThemeColor).alpha!
					}
					return acc
				case 'Font':
					acc[`--${prop}`] = fontOldConverter(value as OldFont, themeFontMap, isScalePropExperimentOn, spx)
					return acc
				case 'BoxShadow':
					acc[`--${prop}`] = boxShadowConverter(value as BoxShadow)
					return acc
				case 'Opacity':
					acc[`--${prop}`] = opacityConverter(value as Opacity)
					return acc
				case 'BackgroundFill':
					acc[`--${prop}`] = backgroundFillConverter(value as BackgroundFill)
					return acc
				case 'Border':
					acc[`--${prop}`] = borderConverter(value as Border)
					return acc
				case 'TextShadow':
					acc[`--${prop}`] = textShadowConverter(value as TextShadow)
					return acc
				case 'TextDecorationLine':
					acc[`--${prop}`] = textDecorationLineConverter(value as TextDecorationLine)
					return acc
				case 'TextAlign':
				case 'TextOutline':
				case 'TextTransform':
				case 'Direction':
				case 'Display':
					acc[`--${prop}`] = (value as CssValue).value
					return acc
				default:
					acc[`--${prop}`] = value as string
			}
			return acc
		},
		{}
	)

	return cssVars
}

export const stylePropsToCssVars = (style: any): Record<string, string | number> => {
	const styleProps = (style as Style).style.properties || (style as Style).style

	const { spx } = style
	const cssVars = _.reduce(
		styleProps,
		(acc: Record<string, string | number>, styleProp, propName) => {
			const converterVars = convertStyleProp(styleProp, propName, spx)
			Object.assign(acc, converterVars)
			return acc
		},
		{}
	)

	return cssVars
}

function convertStyleProp(
	styleProp: StylePropItem | Number | KeywordType<string> | Length,
	propName: string,
	spx: Spx
): Record<string, string> {
	switch (styleProp.type) {
		case 'Font': {
			const font = styleProp as Font
			const fontGranularCompositions = fontConverter(font, spx)
			const fontVars: Record<string, string> = {}

			Object.entries(fontGranularCompositions).forEach(([key, subpropValue]) => {
				if (key === 'font') {
					fontVars[`--${propName}`] = subpropValue
				} else {
					fontVars[`--${propName}-${key}`] = subpropValue
				}
			})

			return fontVars
		}
		// case 'Border': {
		// 	return 'Not implemented yet'
		// }
		// case 'Margin': {
		// 	return 'Not implemented yet'
		// }
		// case 'Background': {
		// 	return 'Not implemented yet'
		// }

		default:
			const defaultConvertor = styleProp.value.toString()
			const convertor = basicStyleConvertersMap[styleProp.type] ?? defaultConvertor
			return { [`--${propName}`]: convertor(styleProp, spx) }
	}
}
